import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PWAUpdateNotification } from "@/components/PWAUpdateNotification";
import { OfflineBanner } from "@/components/OfflineBanner";
import { DraftSyncManager } from "@/components/DraftSyncManager";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { Loader2 } from "lucide-react";

// Core pages - always loaded (needed for initial render)
import Index from "@/pages/Index";
import Auth from "@/pages/Auth";
import NotFound from "@/pages/NotFound";

// Helper to retry dynamic imports with cache-busting
const retryImport = async (
  importFn: () => Promise<{ default: React.ComponentType<any> }>,
  retriesLeft = 3
): Promise<{ default: React.ComponentType<any> }> => {
  try {
    return await importFn();
  } catch (error: any) {
    if (retriesLeft > 0 && error.message?.includes('Failed to fetch dynamically imported module')) {
      console.warn(`Dynamic import failed, retrying... (${retriesLeft} attempts left)`);
      // Wait before retry with exponential backoff
      await new Promise(resolve => setTimeout(resolve, 1000 * (4 - retriesLeft)));
      return retryImport(importFn, retriesLeft - 1);
    }
    
    // All retries failed - force a page reload to clear cache
    console.error('Dynamic import failed after all retries, refreshing page...');
    window.location.reload();
    
    // Return a placeholder while reload happens (won't actually render)
    return {
      default: () => null
    };
  }
};

// Create lazy component with retry logic
const lazyWithRetry = (importFn: () => Promise<{ default: React.ComponentType<any> }>) => {
  return lazy(() => retryImport(importFn));
};

// User pages - lazy loaded with retry logic (reduces initial bundle by ~500KB)
const Projects = lazyWithRetry(() => import("@/pages/Projects"));
const NewProject = lazyWithRetry(() => import("@/pages/NewProject"));
const ProjectDetail = lazyWithRetry(() => import("@/pages/ProjectDetail"));
const Research = lazyWithRetry(() => import("@/pages/Research"));
const AIAssistant = lazyWithRetry(() => import("@/pages/AIAssistant"));
const Templates = lazyWithRetry(() => import("@/pages/Templates"));
const Settings = lazyWithRetry(() => import("@/pages/Settings"));
const Credits = lazyWithRetry(() => import("@/pages/Credits"));
const Install = lazyWithRetry(() => import("@/pages/Install"));

// Admin pages - lazy loaded with retry logic (reduces initial bundle)
const AdminSettings = lazyWithRetry(() => import("@/pages/AdminSettings"));
const AdminUsers = lazyWithRetry(() => import("@/pages/AdminUsers"));
const AdminAuditLogs = lazyWithRetry(() => import("@/pages/AdminAuditLogs"));
const AdminAIAnalytics = lazyWithRetry(() => import("@/pages/AdminAIAnalytics"));
const AdminUserAnalytics = lazyWithRetry(() => import("@/pages/AdminUserAnalytics"));
const AdminAlerts = lazyWithRetry(() => import("@/pages/AdminAlerts"));
const AdminPricing = lazyWithRetry(() => import("@/pages/AdminPricing"));
const AdminPlatformHealth = lazyWithRetry(() => import("@/pages/AdminPlatformHealth"));
const AdminAIPrompts = lazyWithRetry(() => import("@/pages/AdminAIPrompts"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Prevent aggressive refetching on window focus - keeps UI stable
      refetchOnWindowFocus: false,
      // Keep data fresh for 5 minutes before considering it stale
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Loading fallback for lazy-loaded pages
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <PWAUpdateNotification />
            <OfflineBanner />
            <DraftSyncManager />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/install" element={<Suspense fallback={<PageLoader />}><Install /></Suspense>} />
              <Route
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
              <Route path="/projects" element={<Suspense fallback={<PageLoader />}><Projects /></Suspense>} />
                <Route path="/projects/new" element={<Suspense fallback={<PageLoader />}><NewProject /></Suspense>} />
                <Route path="/projects/:id" element={<Suspense fallback={<PageLoader />}><ProjectDetail /></Suspense>} />
                <Route path="/research" element={<Suspense fallback={<PageLoader />}><Research /></Suspense>} />
                <Route path="/ai-assistant" element={<Suspense fallback={<PageLoader />}><AIAssistant /></Suspense>} />
                <Route path="/templates" element={<Suspense fallback={<PageLoader />}><Templates /></Suspense>} />
                <Route path="/settings" element={<Suspense fallback={<PageLoader />}><Settings /></Suspense>} />
                <Route path="/credits" element={<Suspense fallback={<PageLoader />}><Credits /></Suspense>} />
                
                {/* Admin routes - lazy loaded */}
                <Route path="/admin/settings" element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminSettings />
                    </Suspense>
                  </AdminRoute>
                } />
                <Route path="/admin/users" element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminUsers />
                    </Suspense>
                  </AdminRoute>
                } />
                <Route path="/admin/audit-logs" element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminAuditLogs />
                    </Suspense>
                  </AdminRoute>
                } />
                <Route path="/admin/ai-analytics" element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminAIAnalytics />
                    </Suspense>
                  </AdminRoute>
                } />
                <Route path="/admin/user-analytics" element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminUserAnalytics />
                    </Suspense>
                  </AdminRoute>
                } />
                <Route path="/admin/alerts" element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminAlerts />
                    </Suspense>
                  </AdminRoute>
                } />
                <Route path="/admin/pricing" element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPricing />
                    </Suspense>
                  </AdminRoute>
                } />
                <Route path="/admin/platform-health" element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPlatformHealth />
                    </Suspense>
                  </AdminRoute>
                } />
                <Route path="/admin/ai-prompts" element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminAIPrompts />
                    </Suspense>
                  </AdminRoute>
                } />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </TooltipProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
